/* eslint-disable no-else-return */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory, withRouter } from 'react-router-dom';
import { History } from 'history';
import style from './OperationsMenuMobile.module.css';
import {
  setDisconnectFromMasterConfirmationStatus,
  setLanguagesMenuStatus,
  setOpsMenuStatus
} from '../../../redux/actions/setMobileMenusStatus';
import { PAMMState } from '../../../redux/reducers/rootReducer';
import { AccountType } from '../../../types/authTypes';
import LocalStorageUtils from '../../../utils/local-storage';
import { Investor } from '../../../types/investorTypes';
import { useUser } from '../../../utils/useUser';
import { applyStyles } from '../../../utils/applyStyles';
import { ReactComponent as AutoDisIcon } from '../../../icons/mobileMenuIcons/autoDisconnect.svg';
import { ReactComponent as CommissionsIcon } from '../../../icons/mobileMenuIcons/commissions.svg';
import { ReactComponent as CreditInIcon } from '../../../icons/mobileMenuIcons/creditIn.svg';
import { ReactComponent as CreditOutIcon } from '../../../icons/mobileMenuIcons/creditOut.svg';
import { ReactComponent as DepositIcon } from '../../../icons/mobileMenuIcons/deposit.svg';
import { ReactComponent as WithdrawIcon } from '../../../icons/mobileMenuIcons/withdrawal.svg';
import { ReactComponent as DisconnectIcon } from '../../../icons/mobileMenuIcons/disconnect.svg';
import { ReactComponent as HistoryIcon } from '../../../icons/mobileMenuIcons/history.svg';
import { ReactComponent as LanguageIcon } from '../../../icons/mobileMenuIcons/language.svg';
import { ReactComponent as StatementIcon } from '../../../icons/mobileMenuIcons/statement.svg';
import { ReactComponent as ConnectedInvIcon } from '../../../icons/mobileMenuIcons/connectedInvestors.svg';
import { ReactComponent as LogouotIcon } from '../../../icons/mobileMenuIcons/logout.svg';
import { logoutUserAsync } from '../../../redux/actions/auth';
import { FundsType } from '../../../types/masterTypes';

const OperationsMenuMobile: React.FC<any> = ({ onSwipe, onCancel, location, currentLang }): JSX.Element => {
  const dispatch = useDispatch();
  const history: History = useHistory();
  const api: string = useSelector((state: PAMMState) => state.configReducer.api);

  const redirectDepositURL: string = useSelector((state: PAMMState) => state.configReducer.redirectDepositURL || '');
  const redirectWithdrawalURL: string = useSelector((state: PAMMState) => state.configReducer.redirectWithdrawalURL || '');

  const languages: Record<string, string> = useSelector((state: PAMMState) => state.configReducer.languages);
  const investor: Investor = useSelector((state: PAMMState) => state.investorReducer.investor);
  const fundsType = useSelector((state: PAMMState) => state.masterReducer.chosenFundsType);

  const show = useSelector((state: PAMMState) => state.mobileMenusStatusReducer.showOpsMenu);

  const user = useUser();

  const linkedId = LocalStorageUtils.getValueFromLocalStorage('linkedIdMobile') || user?.Id;

  const showDisconnectFromThisMasterWarning =
    investor.MasterId !== 0 && user?.AccountInfo?.AccountOptions?.DetachAction !== 'Off';

  React.useEffect(() => {
    dispatch(setOpsMenuStatus(false));
  }, [dispatch, location]);

  const handleShowLanguagesMenu = (): void => {
    dispatch(setOpsMenuStatus(false));
    dispatch(setLanguagesMenuStatus(true));
  };

  const handleDetachInvestor = (): void => {
    dispatch(setDisconnectFromMasterConfirmationStatus(true));
    dispatch(setOpsMenuStatus(false));
  };

  const handleLogout = (): void => {
    dispatch(setOpsMenuStatus(false));
    dispatch(logoutUserAsync(api, history));
  };

  const getNeededAccountId = () => {
    if (user?.AccountType === AccountType.Master && fundsType === FundsType.TotalFunds) {
      return user?.Id;
    } else {
      return linkedId;
    }
  };

  const showBalanceActions =
    user?.AccountType === AccountType.Investor &&
    (user?.AccountInfo?.AccountOptions?.InvestorDepositButton === 'Show' ||
      user?.AccountInfo?.AccountOptions?.InvestorWithdrawButton === 'Show' ||
      user?.AccountInfo?.AccountOptions?.InvestorCreditButton === 'Show');

  const showRiskManagement =
    (user?.AccountType === AccountType.Investor && showDisconnectFromThisMasterWarning) ||
    (user?.AccountType === AccountType.Investor && user?.AccountInfo?.AccountOptions?.ChangeSlTpButton === 'Show');

  const handleDepositClick = (e: React.MouseEvent) => {
    if (redirectDepositURL) {
      e.preventDefault();
      window.open(redirectDepositURL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleWithdrawClick = (e: React.MouseEvent) => {
    if (redirectWithdrawalURL) {
      e.preventDefault();
      window.open(redirectWithdrawalURL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      id="operations-menu"
      className={applyStyles([style['menu'], show ? style['show'] : style['hide']])}
      onPointerMove={(event) => onSwipe(event)}
      onPointerCancel={() => onCancel('operationsMenu')}
    >
      {showBalanceActions && (
        <section>
          <div className={applyStyles([style['title'], 'text-h4-semibold'])}>
            <FormattedMessage id="actions.accountOperations.label" />
          </div>

          {/* Deposit */}
          {user?.AccountType === AccountType.Investor && user?.AccountInfo?.AccountOptions?.InvestorDepositButton === 'Show' && (
            <Link to={`/change-balance/${getNeededAccountId()}?type=deposit`} onClick={handleDepositClick}>
              <div className={style['row']}>
                <span className={style['icon']}>
                  <DepositIcon />
                </span>
                <span className={applyStyles([style['link'], 'text-h4'])}>
                  <FormattedMessage id="button.deposit" />
                </span>
              </div>
            </Link>
          )}

          {/* Withdraw */}
          {user?.AccountType === AccountType.Investor && user?.AccountInfo?.AccountOptions?.InvestorWithdrawButton === 'Show' && (
            <Link to={`/change-balance/${getNeededAccountId()}?type=withdraw`} onClick={handleWithdrawClick}>
              <div className={style['row']}>
                <span className={style['icon']}>
                  <WithdrawIcon />
                </span>
                <span className={applyStyles([style['link'], 'text-h4'])}>
                  <FormattedMessage id="button.withdraw" />
                </span>
              </div>
            </Link>
          )}

          {/* Credit In */}
          {user?.AccountType === AccountType.Investor && user?.AccountInfo?.AccountOptions?.InvestorCreditButton === 'Show' && (
            <Link to={`/change-credit/${getNeededAccountId()}?type=in`}>
              <div className={style['row']}>
                <span className={style['icon']}>
                  <CreditInIcon />
                </span>
                <span className={applyStyles([style['link'], 'text-h4'])}>
                  <FormattedMessage id="button.creditIn" />
                </span>
              </div>
            </Link>
          )}

          {/* Credit Out */}
          {user?.AccountType === AccountType.Investor && user?.AccountInfo?.AccountOptions?.InvestorCreditButton === 'Show' && (
            <Link to={`/change-credit/${getNeededAccountId()}?type=out`}>
              <div className={style['row']}>
                <span className={style['icon']}>
                  <CreditOutIcon />
                </span>
                <span className={applyStyles([style['link'], 'text-h4'])}>
                  <FormattedMessage id="button.creditOut" />
                </span>
              </div>
            </Link>
          )}
        </section>
      )}

      {/* === Risk Management === */}
      {showRiskManagement && (
        <section>
          <div className={applyStyles([style['title'], 'text-h4-semibold'])}>
            <FormattedMessage id="riskManagement" />
          </div>

          {/* INVESTOR: DISCONNECT */}
          {user?.AccountType === AccountType.Investor && showDisconnectFromThisMasterWarning && (
            <Link to="/#" onClick={handleDetachInvestor}>
              <div className={style['row']}>
                <span className={style['icon']}>
                  <DisconnectIcon />
                </span>
                <span className={applyStyles([style['link'], 'text-h4'])}>
                  <FormattedMessage id="button.disconnect" />
                </span>
              </div>
            </Link>
          )}

          {/* INVESTOR: AUTO DISCONNECT */}
          {user?.AccountType === AccountType.Investor && user?.AccountInfo?.AccountOptions?.ChangeSlTpButton === 'Show' && (
            <Link to={`/edit-slTp/${investor.Id}`}>
              <div className={style['row']}>
                <span className={style['icon']}>
                  <AutoDisIcon />
                </span>
                <span className={applyStyles([style['link'], 'text-h4'])}>
                  <FormattedMessage id="button.autoDisconnect" />
                </span>
              </div>
            </Link>
          )}
        </section>
      )}

      {/* === Information === */}
      <section>
        <div className={applyStyles([style['title'], 'text-h4-semibold'])}>
          <FormattedMessage id="information" />
        </div>

        {/* MASTER and INVESTOR: Orders Commissions */}
        <Link to={`/commissions/${getNeededAccountId()}`}>
          <div className={style['row']}>
            <span className={style['icon']}>
              <CommissionsIcon />
            </span>
            <span className={applyStyles([style['link'], 'text-h4'])}>
              <FormattedMessage id="operations.commissions" />
            </span>
          </div>
        </Link>

        {/* MASTER: Connected Investors */}
        {user?.AccountType === AccountType.Master && user?.AccountInfo?.AccountOptions?.MmConnectedInvestors === 'Show' && (
          <Link to="/investors">
            <div className={style['row']}>
              <span className={style['icon']}>
                <ConnectedInvIcon />
              </span>
              <span className={applyStyles([style['link'], 'text-h4'])}>
                <FormattedMessage id="operations.connectedInvestors" />
              </span>
            </div>
          </Link>
        )}

        {/* History: Always show for INVESTOR and MASTER */}
        <Link to={`/history/${getNeededAccountId()}`}>
          <div className={style['row']}>
            <span className={style['icon']}>
              <HistoryIcon />
            </span>
            <span className={applyStyles([style['link'], 'text-h4'])}>
              <FormattedMessage id="operations.history" />
            </span>
          </div>
        </Link>

        {/* Statements: Condition for INVESTOR, but always show for MASTER */}
        {(user?.AccountInfo?.AccountOptions?.InvestorStatementButton === 'Show' || user?.AccountType === AccountType.Master) && (
          <Link to={`/download-statement/${getNeededAccountId()}`}>
            <div className={style['row']}>
              <span className={style['icon']}>
                <StatementIcon />
              </span>
              <span className={applyStyles([style['link'], 'text-h4'])}>
                <FormattedMessage id="operations.statements" />
              </span>
            </div>
          </Link>
        )}
      </section>

      {/* === Other === */}
      <section>
        <div className={applyStyles([style['title'], 'text-h4-semibold'])}>
          <FormattedMessage id="other" />
        </div>

        {/* Language */}
        <button className={applyStyles([style['row'], 'text-h4'])} onClick={handleShowLanguagesMenu} type="button">
          <span className={style['icon']}>
            <LanguageIcon />
          </span>
          <span className={applyStyles([style['link'], 'text-h4'])}>{languages[currentLang] || languages.en}</span>
        </button>

        {/* Logout */}
        <button className={applyStyles([style['row'], 'text-h4'])} onClick={handleLogout} type="button">
          <span className={style['icon']}>
            <LogouotIcon />
          </span>
          <span className={applyStyles([style['link'], 'text-h4'])}>
            <FormattedMessage id="logout" />
          </span>
        </button>
      </section>
    </div>
  );
};

export default withRouter(OperationsMenuMobile);
